<template>
  <div class="px-3 pt-2 h-full overflow-auto pb-5">
    <!-- MODEL -->
    <div class="border border-gray-200 rounded-8 mb-3 bg-white">
      <button type="button" class="section-header" @click="toggleSection('model')">
        <span>Model</span>
        <Icon name="expand_more" class="w-5 transition-transform" :class="{ 'rotate-180': open.model }" />
      </button>
      <div v-show="open.model" class="px-3 pb-3 pt-1 border-t border-gray-100">
        <FormSelect
          :label="$t('evaluate.provider')"
          v-model="aiPayload.provider"
          :options="providers"
          :selectable="(option) => !option.disabled"
          :reduce="(option) => option.value"
          class="w-full mt-2"
          @update:modelValue="onProviderChange"
        />
        <FormSelect :label="$t('evaluate.model')" v-model="aiPayload.model" :options="models" :reduce="(option) => option.value" class="w-full mt-3" @update:modelValue="onModelChange" />
        <div class="text-11 text-gray-400 mt-1">Only models that support function calling are listed.</div>

        <div class="flex gap-x-3 mt-3">
          <div class="flex-1" v-if="modelSupportsTemperature">
            <FormLabel label="Temperature" />
            <input v-model="aiPayload.temperature" type="number" :min="temperatureRange.min" :max="temperatureRange.max" step="0.1" class="num" @input="emitPayload" @blur="clampTemperature" />
            <div class="text-11 text-gray-400 mt-1">Range {{ temperatureRange.min }}–{{ temperatureRange.max }}</div>
          </div>
          <div class="flex-1" v-else>
            <FormLabel label="Temperature" />
            <div class="text-11 text-gray-400 mt-2">This model only supports its default temperature.</div>
          </div>
          <div class="flex-1">
            <FormLabel label="Max Tokens" />
            <input v-model="aiPayload.max_tokens" type="number" min="100" :max="maxTokensLimit || 32000" step="100" class="num" @input="emitPayload" />
            <div v-if="maxTokensLimit" class="text-11 text-gray-400 mt-1">Max {{ maxTokensLimit }} for this model</div>
          </div>
        </div>
      </div>
    </div>

    <!-- SYSTEM INSTRUCTIONS -->
    <div class="border border-gray-200 rounded-8 mb-3 bg-white">
      <button type="button" class="section-header" @click="toggleSection('instructions')">
        <span>System Instructions</span>
        <Icon name="expand_more" class="w-5 transition-transform" :class="{ 'rotate-180': open.instructions }" />
      </button>
      <div v-show="open.instructions" class="px-3 pb-3 pt-1 border-t border-gray-100">
        <FormSelect
          :label="`${$t('evaluate.persona')} (Optional)`"
          v-model="aiPayload.persona"
          :options="personas"
          :reduce="(option) => option.value"
          class="w-full mt-2"
          show-add-button
          clearable
          @add="onAddPersonaClick"
          @update:modelValue="emitPayload"
        />
        <PhraseEditor v-model="aiPayload.instructions" multiline :items="phraseVariables" class="w-full mt-3" label="Instructions" @update:modelValue="emitPayload" />
      </div>
    </div>

    <!-- INPUTS -->
    <div class="border border-gray-200 rounded-8 mb-3 bg-white">
      <button type="button" class="section-header" @click="toggleSection('inputs')">
        <span>Inputs</span>
        <Icon name="expand_more" class="w-5 transition-transform" :class="{ 'rotate-180': open.inputs }" />
      </button>
      <div v-show="open.inputs" class="px-3 pb-3 pt-2 border-t border-gray-100">
        <div class="text-11 text-gray-400 mb-2">Read-only project variables. Injected into the prompt; toggle "Expose as tool" for large/rarely-used values the agent fetches on demand.</div>
        <FormSelect
          label="Project Variables"
          :modelValue="inputRefs"
          :options="variableOptions"
          :reduce="(option) => option.value"
          multiple
          auto-flip
          class="w-full"
          @update:modelValue="setInputRefs"
        />
        <div v-for="inp in aiPayload.inputs" :key="inp.ref" class="flex items-center justify-between mt-2 px-1">
          <span class="text-13 text-gray-700">{{ inp.ref }}</span>
          <label class="flex items-center gap-2 text-12 text-gray-500 cursor-pointer select-none">
            <input type="checkbox" :checked="inp.expose_as_tool" @change="toggleExpose(inp, $event.target.checked)" />
            Expose as tool
          </label>
        </div>
      </div>
    </div>

    <!-- SESSION MEMORY -->
    <div class="border border-gray-200 rounded-8 mb-3 bg-white">
      <button type="button" class="section-header" @click="toggleSection('session')">
        <span>Session Memory</span>
        <Icon name="expand_more" class="w-5 transition-transform" :class="{ 'rotate-180': open.session }" />
      </button>
      <div v-show="open.session" class="px-3 pb-3 pt-1 border-t border-gray-100">
        <FormSelect
          label="Session Store (Optional — ephemeral if unset)"
          v-model="aiPayload.session.adapter"
          :options="adapterOptions"
          :reduce="(option) => option.value"
          clearable
          class="w-full mt-2"
          @update:modelValue="emitPayload"
        />
        <div class="flex gap-x-3 mt-3">
          <div class="flex-1">
            <FormSelect
              label="Compaction"
              v-model="aiPayload.session.compaction.strategy"
              :options="strategyOptions"
              :reduce="(option) => option.value"
              :clearable="false"
              @update:modelValue="emitPayload"
            />
          </div>
          <div class="flex-1">
            <FormLabel label="Threshold (0-1)" />
            <input v-model="aiPayload.session.compaction.threshold" type="number" min="0.1" max="0.95" step="0.05" class="num" @input="emitPayload" />
          </div>
        </div>
        <div class="flex gap-x-3 mt-3">
          <div class="flex-1">
            <FormLabel label="Keep Recent Turns" />
            <input v-model="aiPayload.session.compaction.keep_recent_turns" type="number" min="1" max="50" step="1" class="num" @input="emitPayload" />
          </div>
          <div class="flex-1">
            <FormLabel label="Summary Max Tokens" />
            <input v-model="aiPayload.session.compaction.summary_max_tokens" type="number" min="128" max="8000" step="128" class="num" @input="emitPayload" />
          </div>
        </div>
      </div>
    </div>

    <!-- RESPONSE -->
    <div class="border border-gray-200 rounded-8 mb-3 bg-white">
      <button type="button" class="section-header" @click="toggleSection('response')">
        <span>Response</span>
        <Icon name="expand_more" class="w-5 transition-transform" :class="{ 'rotate-180': open.response }" />
      </button>
      <div v-show="open.response" class="px-3 pb-3 pt-1 border-t border-gray-100">
        <FormSelect
          label="Mode"
          v-model="aiPayload.response.mode"
          :options="responseModeOptions"
          :reduce="(option) => option.value"
          :clearable="false"
          class="w-full mt-2"
          @update:modelValue="emitPayload"
        />
        <VariableSelect
          v-if="aiPayload.response.mode !== 'chat'"
          label="Output Variable (Optional — for use inside a workflow)"
          v-model="aiPayload.output_variable"
          class="w-full mt-3"
          clearable
          @update:modelValue="emitPayload"
        />
      </div>
    </div>

    <!-- HUMAN-IN-THE-LOOP -->
    <div class="border border-gray-200 rounded-8 mb-3 bg-white">
      <button type="button" class="section-header" @click="toggleSection('hitl')">
        <span>Human-in-the-loop</span>
        <Icon name="expand_more" class="w-5 transition-transform" :class="{ 'rotate-180': open.hitl }" />
      </button>
      <div v-show="open.hitl" class="px-3 pb-3 pt-2 border-t border-gray-100">
        <div class="text-11 text-gray-400 mb-2">
          Tools with the "Require approval" policy pause until approved. Choose how approvals are requested — these are independent (the approver is often not the chat user).
        </div>

        <label class="flex items-center gap-2 text-13 text-gray-700 cursor-pointer select-none">
          <input type="checkbox" v-model="aiPayload.hitl.chat" @change="emitPayload" />
          In-chat approval — the user approves inline in the conversation
        </label>
        <label class="flex items-center gap-2 text-13 mt-2 cursor-pointer select-none" :class="{ 'text-gray-400 cursor-not-allowed': !hasEmailProvider, 'text-gray-700': hasEmailProvider }">
          <input type="checkbox" v-model="aiPayload.hitl.email" :disabled="!hasEmailProvider" @change="emitPayload" />
          Email approval — external approvers approve via emailed links
        </label>
        <div v-if="!hasEmailProvider" class="text-11 text-gray-400 mt-1">Enable an email provider to use email approvals.</div>

        <template v-if="aiPayload.hitl.email && hasEmailProvider">
          <div class="flex gap-x-3 items-start mt-3">
            <FormSelectMultiBadge
              v-model="aiPayload.hitl.from"
              label="From"
              :options="emailFromList"
              searchable
              :clearable="false"
              :class="hitlFromIsDomain ? 'w-1/2' : 'w-full'"
              @update:modelValue="emitPayload"
            />
            <div v-if="hitlFromIsDomain" class="w-1/2 flex flex-row items-center">
              <FormInput v-model="aiPayload.hitl.fromEmail" name="hitl_from_em" label="From Email" class="flex-grow" @update:modelValue="emitPayload" />
              <div class="text-13 mt-4 ml-1 whitespace-nowrap">@{{ aiPayload.hitl.from.value }}</div>
            </div>
          </div>
          <PhraseEditor label="Approver Emails (comma-separated)" v-model="approversText" :items="phraseVariables" class="w-full mt-3" @update:modelValue="onApproversChange" />
          <FormSelect
            label="Approval Email Template"
            v-model="aiPayload.hitl.approval_template"
            :options="templateOptions"
            :reduce="(option) => option.value"
            clearable
            class="w-full mt-3"
            @update:modelValue="emitPayload"
          />
        </template>

        <div class="flex gap-x-3 mt-3">
          <div class="flex-1">
            <FormLabel label="Approval Timeout (ms)" />
            <input v-model="aiPayload.hitl.timeout_ms" type="number" min="60000" step="60000" class="num" @input="emitPayload" />
          </div>
          <div class="flex-1">
            <FormSelect label="On Timeout" v-model="aiPayload.hitl.on_timeout" :options="onTimeoutOptions" :reduce="(option) => option.value" :clearable="false" @update:modelValue="emitPayload" />
          </div>
        </div>
      </div>
    </div>

    <!-- LIMITS -->
    <div class="border border-gray-200 rounded-8 mb-3 bg-white">
      <button type="button" class="section-header" @click="toggleSection('limits')">
        <span>Limits</span>
        <Icon name="expand_more" class="w-5 transition-transform" :class="{ 'rotate-180': open.limits }" />
      </button>
      <div v-show="open.limits" class="px-3 pb-3 pt-2 border-t border-gray-100">
        <div class="flex gap-x-3">
          <div class="flex-1">
            <FormLabel label="Max Iterations" />
            <input v-model="aiPayload.limits.max_iterations" type="number" min="1" max="50" step="1" class="num" @input="emitPayload" />
          </div>
          <div class="flex-1">
            <FormLabel label="Max Tool Calls" />
            <input v-model="aiPayload.limits.max_tool_calls" type="number" min="1" max="200" step="1" class="num" @input="emitPayload" />
          </div>
        </div>
        <FormLabel label="Timeout (ms)" class="mt-3" />
        <input v-model="aiPayload.limits.timeout_ms" type="number" min="10000" step="10000" class="num" @input="emitPayload" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import CreatePersonaModal from '@/components/project/CreatePersonaModal.vue';
import EvaluateItemsConfig from '@/components/flow/evaluate/EvaluateItemsConfig';
import useAIModelSource from '@/components/flow/evaluate/useAIModelSource';
import { AI_FEATURES, DATA_ADAPTERS } from '@/constants';
import debounce from '../../../helpers/debounce';

export default {
  name: 'AIAgentConfig',
  mixins: [useAIModelSource],
  props: {
    item: { type: Object, default: null },
  },
  data() {
    return {
      aiPayload: structuredClone(EvaluateItemsConfig['ai-agent'].payload),
      approversText: '',
      // Collapsible sections — model + instructions open by default to reduce clutter.
      open: { model: true, instructions: true, inputs: false, session: false, response: false, hitl: false, limits: false },
    };
  },
  computed: {
    ...mapState(['availableAIProviders']),
    ...mapGetters(['getProjectAIProviderOptions', 'getProjectAIProviders', 'getProjectPersonas', 'getProjectVariables', 'getProjectDataAdapters', 'getProjectTemplates', 'getProjectEmailProviders']),
    payload() {
      return { id: this.item.id, type: this.item.type, payload: this.aiPayload };
    },
    // Selectable senders for email approvals (mirrors the send_email block).
    emailFromList() {
      return Object.values(this.getProjectEmailProviders() || {}).reduce((acc, item) => {
        const accounts = item?.config?.accounts || [];
        return [
          ...acc,
          ...accounts.map((account) => ({
            label: account.email || account.domain,
            value: account.email || account.domain,
            provider: item.provider,
            badge1: account.email ? 'email' : 'domain',
            badge2: item.provider,
          })),
        ];
      }, []);
    },
    hasEmailProvider() {
      return this.emailFromList.length > 0;
    },
    hitlFromIsDomain() {
      return this.aiPayload.hitl.from && this.aiPayload.hitl.from.badge1 === 'domain';
    },
    variableOptions() {
      return Object.keys(this.getProjectVariables()).map((i) => ({ label: i, value: i }));
    },
    phraseVariables() {
      return Object.keys(this.getProjectVariables()).map((i) => ({ label: i, value: i }));
    },
    providers() {
      // Only providers that expose at least one function/tool-calling model can
      // back an agent (excludes e.g. Perplexity, whose Sonar models have none).
      return this.getProjectAIProviderOptions().filter((option) => this.hasAgentCapableModel(option.value));
    },
    models() {
      if (!this.aiPayload.provider || !this.availableAIProviders?.[this.aiPayload.provider]) return [];
      return this.agentCapableModels(this.aiPayload.provider).map((item) => ({
        label: `<b>${item.name}</b> - <i class="text-11">Cost: ${item.cost}</i>`,
        value: item.model,
      }));
    },
    temperatureRange() {
      const range = this.aiPayload.model_config?.temperature;
      return range && typeof range === 'object' ? range : { min: 0, max: 2, default: 1 };
    },
    modelSupportsTemperature() {
      // No model_config yet (legacy/unselected) → allow editing with a default range.
      if (!this.aiPayload.model_config) return true;
      return !!this.aiPayload.model_config.temperature;
    },
    maxTokensLimit() {
      // The selected model's max output tokens from the provider registry
      // (model_config is a clone of that model object). Null until a model is set.
      return this.aiPayload.model_config?.max_tokens || null;
    },
    personas() {
      return Object.values(this.getProjectPersonas()).map((item) => ({ label: item.name, value: item.persona_id }));
    },
    adapterOptions() {
      return Object.keys(this.getProjectDataAdapters() || {}).map((k) => ({ label: (DATA_ADAPTERS[k] && DATA_ADAPTERS[k].name) || k, value: k }));
    },
    templateOptions() {
      return Object.values(this.getProjectTemplates() || {}).map((t) => ({ label: t.name || t.template_id || t.id, value: t.template_id || t.id }));
    },
    inputRefs() {
      return (this.aiPayload.inputs || []).map((i) => i.ref);
    },
    projectId() {
      return this.$route.params.projectId;
    },
    strategyOptions() {
      return [
        { label: 'By tokens', value: 'token' },
        { label: 'By turns', value: 'turns' },
      ];
    },
    responseModeOptions() {
      return [
        { label: 'Stream (interactive chat)', value: 'stream' },
        { label: 'Final (programmatic / email / API)', value: 'final' },
      ];
    },
    onTimeoutOptions() {
      return [
        { label: 'Reject', value: 'reject' },
        { label: 'Cancel', value: 'cancel' },
      ];
    },
  },
  methods: {
    toggleSection(key) {
      this.open[key] = !this.open[key];
    },
    // eslint-disable-next-line
    emitPayload: debounce(function () {
      this.$emit('update', this.payload);
    }, 300),
    // Models usable as an agent: those advertising function/tool calling. Works
    // for both fixed providers and dynamic (LM Studio) snapshots via the mixin.
    agentCapableModels(provider) {
      return this.modelsForProvider(provider).filter((model) => (model.features || []).includes(AI_FEATURES.FUNCTION_CALLING));
    },
    hasAgentCapableModel(provider) {
      return this.agentCapableModels(provider).length > 0;
    },
    onProviderChange() {
      this.aiPayload.model = '';
      this.aiPayload.model_config = null;
      this.emitPayload();
    },
    // Resolve the selected model's full config object from the provider registry,
    // merged with the provider-level request fields the runtime needs. This object
    // is published with the project so the runtime can shape requests per-model.
    buildModelConfig(provider, modelId) {
      if (!provider || !modelId) return null;
      return this.buildDynamicAwareModelConfig(provider, modelId);
    },
    onModelChange() {
      this.aiPayload.model_config = this.buildModelConfig(this.aiPayload.provider, this.aiPayload.model);
      const range = this.aiPayload.model_config?.temperature;
      if (range && typeof range === 'object') {
        const cur = parseFloat(this.aiPayload.temperature);
        const next = Number.isFinite(cur) ? Math.min(Math.max(cur, range.min), range.max) : range.default ?? 1;
        this.aiPayload.temperature = String(next);
      }
      this.emitPayload();
    },
    clampTemperature() {
      const { min, max, default: def } = this.temperatureRange;
      const cur = parseFloat(this.aiPayload.temperature);
      const next = Number.isFinite(cur) ? Math.min(Math.max(cur, min), max) : def ?? min;
      this.aiPayload.temperature = String(next);
      this.emitPayload();
    },
    onAddPersonaClick() {
      this.$showModal(CreatePersonaModal, { projectId: this.projectId });
    },
    onApproversChange(value) {
      this.aiPayload.hitl.approvers = (value || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      this.emitPayload();
    },
    setInputRefs(refs) {
      const existing = {};
      (this.aiPayload.inputs || []).forEach((i) => {
        existing[i.ref] = i;
      });
      this.aiPayload.inputs = (refs || []).map((ref) => existing[ref] || { ref, expose_as_tool: false });
      this.emitPayload();
    },
    toggleExpose(inp, checked) {
      inp.expose_as_tool = checked;
      this.emitPayload();
    },
  },
  watch: {
    item: {
      handler() {
        if (!this.item) return;
        const defaults = structuredClone(EvaluateItemsConfig['ai-agent'].payload);
        const loaded = JSON.parse(JSON.stringify(this.item.payload || {}));

        // Backward-compat with the legacy ai-agent payload.
        if (loaded.context && !loaded.instructions) loaded.instructions = loaded.context;
        if (Array.isArray(loaded.context_variables) && !loaded.inputs) {
          loaded.inputs = loaded.context_variables.map((ref) => ({ ref, expose_as_tool: false }));
        }

        this.aiPayload = {
          ...defaults,
          ...loaded,
          tools: Array.isArray(loaded.tools) ? loaded.tools : [],
          inputs: Array.isArray(loaded.inputs) ? loaded.inputs : [],
          session: {
            ...defaults.session,
            ...(loaded.session || {}),
            compaction: { ...defaults.session.compaction, ...((loaded.session && loaded.session.compaction) || {}) },
          },
          response: { ...defaults.response, ...(loaded.response || {}) },
          hitl: { ...defaults.hitl, ...(loaded.hitl || {}) },
          limits: { ...defaults.limits, ...(loaded.limits || {}) },
        };
        // Ensure the published model config matches the currently selected model
        // (covers agents saved before model_config existed, or registry updates).
        if (this.aiPayload.model && this.aiPayload.model_config?.model !== this.aiPayload.model) {
          this.aiPayload.model_config = this.buildModelConfig(this.aiPayload.provider, this.aiPayload.model);
        }
        this.approversText = (this.aiPayload.hitl?.approvers || []).join(', ');
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style scoped>
.num {
  width: 100%;
  border: 1px solid var(--stroke, #e5e0f0);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 13px;
}
.num:focus {
  outline: none;
  border-color: var(--primary, #6b4eff);
}
.section-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.75rem;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  user-select: none;
}
.section-header:hover {
  color: #4b5563;
}
</style>
